/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio State Management Store (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { useWalletStore } from './useWalletStore';

export interface ImageModelOption {
  id: string;
  name: string;
  provider: string;
  creditCost: number;
  tag: string;
  description: string;
  badgeVariant?: 'primary' | 'accent' | 'emerald';
}

export interface AspectRatioOption {
  id: string;
  label: string;
  subLabel: string;
  ratio: string;
  width: number;
  height: number;
}

export interface StylePresetOption {
  id: string;
  name: string;
  previewUrl: string;
  promptSuffix: string;
  negativeSuffix?: string;
}

export interface GeneratedImageItem {
  id: string;
  url: string;
  prompt: string;
  negativePrompt?: string;
  model: string;
  modelName: string;
  aspectRatio: string;
  width: number;
  height: number;
  style: string;
  seed: number;
  creditsUsed: number;
  createdAt: string;
  isFavorite: boolean;
}

export interface ImageStudioState {
  // Input settings
  prompt: string;
  negativePrompt: string;
  selectedModel: string;
  aspectRatio: string;
  stylePreset: string;
  imageCount: number; // 1, 2, 4
  seed: number | null;
  guidanceScale: number; // 3.5 - 12 (default 7.5)

  // Status & Pipeline
  isGenerating: boolean;
  isEnhancingPrompt: boolean;
  generationProgress: number; // 0 - 100%
  generationStage: string;
  errorMessage: string | null;

  // History & Gallery Lightbox
  historyImages: GeneratedImageItem[];
  activeLightboxImage: GeneratedImageItem | null;
  filterFavoriteOnly: boolean;

  // Actions
  setPrompt: (prompt: string) => void;
  setNegativePrompt: (negativePrompt: string) => void;
  setSelectedModel: (modelId: string) => void;
  setAspectRatio: (aspectRatio: string) => void;
  setStylePreset: (stylePreset: string) => void;
  setImageCount: (count: number) => void;
  setSeed: (seed: number | null) => void;
  setGuidanceScale: (scale: number) => void;
  setFilterFavoriteOnly: (filter: boolean) => void;

  // Pipeline Operations
  enhancePromptWithAI: () => Promise<void>;
  generateImages: () => Promise<boolean>;
  openLightbox: (image: GeneratedImageItem) => void;
  closeLightbox: () => void;
  toggleFavoriteImage: (id: string) => void;
  deleteImage: (id: string) => void;
  reusePrompt: (image: GeneratedImageItem) => void;
  resetForm: () => void;
}

export const IMAGE_MODELS: ImageModelOption[] = [
  {
    id: 'flux-1.1-pro',
    name: 'Flux 1.1 Pro',
    provider: 'Black Forest Labs',
    creditCost: 2.5,
    tag: 'Đỉnh cao',
    badgeVariant: 'accent',
    description: 'Chi tiết siêu nét, kết xuất chữ typography hoàn hảo, độ trung thực hình ảnh cao nhất.',
  },
  {
    id: 'flux-dev',
    name: 'Flux Dev',
    provider: 'Black Forest Labs',
    creditCost: 1.5,
    tag: 'Cân bằng',
    badgeVariant: 'primary',
    description: 'Tốc độ sinh nhanh, đa dạng phong cách, phù hợp tạo concept ý tưởng.',
  },
  {
    id: 'dall-e-3',
    name: 'OpenAI DALL-E 3',
    provider: 'OpenAI',
    creditCost: 3.0,
    tag: 'Sáng tạo',
    badgeVariant: 'primary',
    description: 'Khả năng hiểu ngữ nghĩa ngôn ngữ tự nhiên xuất sắc, bố cục rực rỡ.',
  },
  {
    id: 'sd-3.5-large',
    name: 'Stable Diffusion 3.5',
    provider: 'Stability AI',
    creditCost: 1.8,
    tag: 'Chi tiết cao',
    badgeVariant: 'emerald',
    description: 'Giải phẫu người chuẩn xác, hỗ trợ tùy biến ánh sáng và bố cục phức tạp.',
  },
  {
    id: 'midjourney-proxy',
    name: 'ModelsLab Midjourney',
    provider: 'ModelsLab',
    creditCost: 2.2,
    tag: 'Điện ảnh',
    badgeVariant: 'accent',
    description: 'Thẩm mỹ điện ảnh nghệ thuật, màu sắc giàu cảm xúc và kịch tính.',
  },
];

export const ASPECT_RATIOS: AspectRatioOption[] = [
  {
    id: '1:1',
    label: '1:1 Vuông',
    subLabel: 'Avatar & Instagram',
    ratio: '1:1',
    width: 1024,
    height: 1024,
  },
  {
    id: '16:9',
    label: '16:9 Ngang',
    subLabel: 'Youtube & Website Banner',
    ratio: '16:9',
    width: 1344,
    height: 768,
  },
  {
    id: '9:16',
    label: '9:16 Dọc',
    subLabel: 'TikTok & Story/Reels',
    ratio: '9:16',
    width: 768,
    height: 1344,
  },
  {
    id: '4:3',
    label: '4:3 Chân Dung',
    subLabel: 'Nhiếp ảnh cổ điển',
    ratio: '4:3',
    width: 1152,
    height: 864,
  },
  {
    id: '21:9',
    label: '21:9 Siêu Rộng',
    subLabel: 'Điện ảnh Ultrawide',
    ratio: '21:9',
    width: 1536,
    height: 640,
  },
];

export const STYLE_PRESETS: StylePresetOption[] = [
  {
    id: 'none',
    name: 'Nguyên Bản',
    previewUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=200&auto=format&fit=crop&q=80',
    promptSuffix: '',
  },
  {
    id: 'photorealistic',
    name: 'Chân Thực 8K',
    previewUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&auto=format&fit=crop&q=80',
    promptSuffix: ', highly detailed photorealistic, 8k resolution, Hasselblad medium format photo, natural studio lighting, ultra sharp focus, realistic textures',
    negativeSuffix: ', 3d render, cartoon, blurry, deformed limbs, smooth plastic skin',
  },
  {
    id: 'cinematic',
    name: 'Điện Ảnh',
    previewUrl: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=200&auto=format&fit=crop&q=80',
    promptSuffix: ', cinematic movie still, 35mm film photography, dramatic atmospheric volumetric lighting, anamorphic lens flare, shallow depth of field, color graded',
    negativeSuffix: ', amateur snapshot, overexposed, low quality',
  },
  {
    id: '3d-pixar',
    name: 'Hoạt Hình 3D',
    previewUrl: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=200&auto=format&fit=crop&q=80',
    promptSuffix: ', cute modern 3D Pixar animation character style, Octane render, raytraced subsurface scattering, vibrant colors, expressive eyes, smooth polished shapes',
    negativeSuffix: ', realistic photo, horror, gritty, jagged',
  },
  {
    id: 'anime',
    name: 'Anime Nhật',
    previewUrl: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=200&auto=format&fit=crop&q=80',
    promptSuffix: ', Japanese anime style, Makoto Shinkai aesthetic, Studio Ghibli inspired, vibrant sunset sky, delicate outlines, beautiful cloud formations, atmospheric masterpiece',
    negativeSuffix: ', western cartoon, 3d, realistic photograph',
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk Neon',
    previewUrl: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=200&auto=format&fit=crop&q=80',
    promptSuffix: ', cyberpunk futuristic sci-fi aesthetic, vibrant cyan and magenta neon lights, wet asphalt rain reflections, holographic billboards, high tech urban street',
    negativeSuffix: ', sunny daylight, pastel, rustic nature',
  },
  {
    id: 'oil-painting',
    name: 'Sơn Dầu Cổ Điển',
    previewUrl: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b675?w=200&auto=format&fit=crop&q=80',
    promptSuffix: ', classical masterpiece oil on canvas, heavy impasto brushstrokes, textured canvas, Rembrandt chiaroscuro lighting, rich deep pigments',
    negativeSuffix: ', digital flat vector, photo, 3d render',
  },
];

const INITIAL_GALLERY_IMAGES: GeneratedImageItem[] = [
  {
    id: 'img_flux_01',
    url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&auto=format&fit=crop&q=80',
    prompt: 'Floating futuristic glass sphere containing a miniature blooming sakura garden, soft dawn light, edge reflections, 8k ultra realistic',
    model: 'flux-1.1-pro',
    modelName: 'Flux 1.1 Pro',
    aspectRatio: '1:1',
    width: 1024,
    height: 1024,
    style: 'photorealistic',
    seed: 489214,
    creditsUsed: 2.5,
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    isFavorite: true,
  },
  {
    id: 'img_flux_02',
    url: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1200&auto=format&fit=crop&q=80',
    prompt: 'Cinematic wide shot of an astronaut standing on the edge of a crystalline red canyon on Mars at sunset, dust storm on the horizon',
    model: 'dall-e-3',
    modelName: 'OpenAI DALL-E 3',
    aspectRatio: '16:9',
    width: 1344,
    height: 768,
    style: 'cinematic',
    seed: 712903,
    creditsUsed: 3.0,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    isFavorite: false,
  },
  {
    id: 'img_flux_03',
    url: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1200&auto=format&fit=crop&q=80',
    prompt: 'A tiny cute robot barista brewing espresso in a cozy wooden coffee shop, warm yellow cafe lights, steam rising from ceramic cup',
    model: 'flux-dev',
    modelName: 'Flux Dev',
    aspectRatio: '4:3',
    width: 1152,
    height: 864,
    style: '3d-pixar',
    seed: 391820,
    creditsUsed: 1.5,
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    isFavorite: true,
  },
];

const STORAGE_KEY_GALLERY = 'sophia_image_studio_gallery';

function loadStoredImages(): GeneratedImageItem[] {
  if (typeof window === 'undefined') return INITIAL_GALLERY_IMAGES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_GALLERY);
    if (!raw) return INITIAL_GALLERY_IMAGES;
    return JSON.parse(raw);
  } catch {
    return INITIAL_GALLERY_IMAGES;
  }
}

function saveStoredImages(images: GeneratedImageItem[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY_GALLERY, JSON.stringify(images));
  } catch (err) {
    console.error('Failed to save gallery to localStorage:', err);
  }
}

export const useImageStudioStore = create<ImageStudioState>((set, get) => ({
  prompt: 'A futuristic floating cyber city covered in neon signs and flying vehicles during a golden hour rainstorm',
  negativePrompt: 'blurry, low quality, distorted, extra limbs, bad anatomy, watermark',
  selectedModel: 'flux-1.1-pro',
  aspectRatio: '16:9',
  stylePreset: 'cinematic',
  imageCount: 1,
  seed: null,
  guidanceScale: 7.5,

  isGenerating: false,
  isEnhancingPrompt: false,
  generationProgress: 0,
  generationStage: 'Ready',
  errorMessage: null,

  historyImages: loadStoredImages(),
  activeLightboxImage: null,
  filterFavoriteOnly: false,

  setPrompt: (prompt) => set({ prompt, errorMessage: null }),

  setNegativePrompt: (negativePrompt) => set({ negativePrompt }),

  setSelectedModel: (selectedModel) => set({ selectedModel }),

  setAspectRatio: (aspectRatio) => set({ aspectRatio }),

  setStylePreset: (stylePreset) => set({ stylePreset }),

  setImageCount: (imageCount) => set({ imageCount }),

  setSeed: (seed) => set({ seed }),

  setGuidanceScale: (guidanceScale) => set({ guidanceScale }),

  setFilterFavoriteOnly: (filterFavoriteOnly) => set({ filterFavoriteOnly }),

  enhancePromptWithAI: async () => {
    const { prompt } = get();
    if (!prompt.trim() || get().isEnhancingPrompt) return;

    set({ isEnhancingPrompt: true });
    // Simulate prompt enrichment using edge LLM
    await new Promise((r) => setTimeout(r, 600));

    const enriched = `${prompt.trim()}, intricate details, cinematic lighting, volumetric atmosphere, 8k resolution, photorealistic textures, master composition, award-winning photography, octane render style`;

    set({
      prompt: enriched,
      isEnhancingPrompt: false,
    });
  },

  generateImages: async () => {
    const {
      prompt,
      negativePrompt,
      selectedModel,
      aspectRatio,
      stylePreset,
      imageCount,
      seed,
      isGenerating,
    } = get();

    if (!prompt.trim() || isGenerating) return false;

    const modelObj = IMAGE_MODELS.find((m) => m.id === selectedModel) || IMAGE_MODELS[0];
    const totalCost = modelObj.creditCost * imageCount;

    // Check wallet balance
    const currentBalance = useWalletStore.getState().balance;
    if (currentBalance < totalCost) {
      set({
        errorMessage: `Số dư ví không đủ (${totalCost} tín chỉ). Vui lòng nạp thêm tín chỉ để tiếp tục!`,
      });
      return false;
    }

    // Deduct wallet balance optimistically
    const deductResult = useWalletStore.getState().optimisticDeduct(totalCost);
    if (!deductResult.success) {
      set({
        errorMessage: `Số dư ví không đủ (${totalCost} tín chỉ). Vui lòng nạp thêm tín chỉ để tiếp tục!`,
      });
      return false;
    }

    set({
      isGenerating: true,
      generationProgress: 15,
      generationStage: 'Queued in Cloudflare Edge Queue...',
      errorMessage: null,
    });

    // 1. Queue step
    await new Promise((r) => setTimeout(r, 400));
    set({
      generationProgress: 45,
      generationStage: 'Denoising Diffusion sampling...',
    });

    // 2. Diffusion processing
    await new Promise((r) => setTimeout(r, 800));
    set({
      generationProgress: 85,
      generationStage: 'Uploading to Cloudflare R2 ($0 Egress)...',
    });

    // 3. R2 Upload & finalize
    await new Promise((r) => setTimeout(r, 400));

    const ratioObj = ASPECT_RATIOS.find((r) => r.id === aspectRatio) || ASPECT_RATIOS[0];
    const styleObj = STYLE_PRESETS.find((s) => s.id === stylePreset);

    // Build newly created image items
    const newItems: GeneratedImageItem[] = [];
    const now = new Date().toISOString();

    for (let i = 0; i < imageCount; i++) {
      const randomSeed = seed !== null ? seed + i : Math.floor(Math.random() * 900000) + 100000;
      // High-resolution Unsplash sample corresponding to modern AI aesthetics
      const sampleUrls = [
        'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1200&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1200&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1200&auto=format&fit=crop&q=80',
      ];
      const assignedUrl = sampleUrls[(get().historyImages.length + i) % sampleUrls.length];

      newItems.push({
        id: `img_${Date.now()}_${i}`,
        url: assignedUrl,
        prompt: prompt + (styleObj?.promptSuffix || ''),
        negativePrompt: negativePrompt + (styleObj?.negativeSuffix || ''),
        model: modelObj.id,
        modelName: modelObj.name,
        aspectRatio: ratioObj.ratio,
        width: ratioObj.width,
        height: ratioObj.height,
        style: stylePreset,
        seed: randomSeed,
        creditsUsed: modelObj.creditCost,
        createdAt: now,
        isFavorite: false,
      });
    }

    const updatedGallery = [...newItems, ...get().historyImages];
    saveStoredImages(updatedGallery);

    set({
      isGenerating: false,
      generationProgress: 100,
      generationStage: 'Ready',
      historyImages: updatedGallery,
      activeLightboxImage: newItems[0], // Auto-open first generated image in lightbox
    });

    return true;
  },

  openLightbox: (image) => set({ activeLightboxImage: image }),

  closeLightbox: () => set({ activeLightboxImage: null }),

  toggleFavoriteImage: (id) => {
    const updated = get().historyImages.map((img) =>
      img.id === id ? { ...img, isFavorite: !img.isFavorite } : img
    );
    saveStoredImages(updated);
    set((state) => ({
      historyImages: updated,
      activeLightboxImage:
        state.activeLightboxImage?.id === id
          ? { ...state.activeLightboxImage, isFavorite: !state.activeLightboxImage.isFavorite }
          : state.activeLightboxImage,
    }));
  },

  deleteImage: (id) => {
    const updated = get().historyImages.filter((img) => img.id !== id);
    saveStoredImages(updated);
    set((state) => ({
      historyImages: updated,
      activeLightboxImage: state.activeLightboxImage?.id === id ? null : state.activeLightboxImage,
    }));
  },

  reusePrompt: (image) => {
    set({
      prompt: image.prompt,
      negativePrompt: image.negativePrompt || '',
      selectedModel: image.model,
      aspectRatio: image.aspectRatio,
      stylePreset: image.style,
      seed: image.seed,
    });
  },

  resetForm: () => {
    set({
      prompt: '',
      negativePrompt: 'blurry, low quality, distorted, extra limbs, bad anatomy, watermark',
      selectedModel: 'flux-1.1-pro',
      aspectRatio: '16:9',
      stylePreset: 'cinematic',
      imageCount: 1,
      seed: null,
      guidanceScale: 7.5,
      errorMessage: null,
    });
  },
}));
